// v0.5.2 — Family-B "Amend / reclassify" action for Art 45
// seller-country reclassified rows. Adds a second resolve path
// alongside "Acknowledge & keep": toggle the Tulica FX treatment
// between INCLUSIVE (extract VAT out of the stated amount, the
// default) and RE-INVOICED (stated amount is net, VAT added on top).
// Re-invoiced requires a credit-note / reissued-invoice reference.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { reclassifyFxTreatment } from "../../lib/api";
import { errorMessage } from "../../lib/errorMessage";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "../ui/sonner";
import { PresetReasonSelect } from "../PresetReasonSelect";
import { FX_TREATMENT_REASONS } from "../../lib/reviewReasonPresets";

export const FxTreatmentAmend = ({ tid, calcId, reinvoiced, onResolved }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [treatment, setTreatment] = useState(reinvoiced ? "reinvoiced" : "inclusive");
  const [evidence, setEvidence] = useState("");
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const reset = () => {
    setTreatment(reinvoiced ? "reinvoiced" : "inclusive");
    setEvidence(""); setReason(""); setPreset(""); setErr(null);
  };

  // v0.6.12 A5-27 — the two treatments are opposite facts, so each has
  // its own reasons; switching clears a reason picked for the other one.
  const pickTreatment = (next) => {
    if (next === treatment) return;
    setTreatment(next); setPreset(""); setReason("");
  };

  const cleaned = reason.trim();
  const needsEvidence = treatment === "reinvoiced";
  const valid = cleaned.length >= 8 && !/[<>]/.test(cleaned)
    && (!needsEvidence || evidence.trim().length > 0);

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true); setErr(null);
    try {
      await reclassifyFxTreatment(calcId, {
        txn_id: tid,
        treatment,
        evidence_ref: needsEvidence ? evidence.trim() : "",
        reason: cleaned,
      });
      setOpen(false); reset();
      toast.success(
        t("ui.fx_amend.toast_ok", { defaultValue: "FX treatment amended" }),
        { description: `${tid.slice(0, 12)}… → ${treatment}` },
      );
      if (onResolved) await onResolved();
    } catch (e) {
      const msg = errorMessage(e);
      setErr(msg);
      toast.error(
        t("ui.fx_amend.toast_fail", { defaultValue: "Could not amend treatment" }),
        { description: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`fx-amend-open-${tid}`}
          data-reinvoiced={reinvoiced ? "true" : "false"}
          className="text-[10px] tabular-nums px-2 py-1 rounded border border-teal-400 dark:border-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/40 text-teal-700 dark:text-teal-200"
        >
          {reinvoiced
            ? t("ui.fx_amend.badge_reinvoiced", { defaultValue: "Amend · re-invoiced" })
            : t("ui.fx_amend.btn", { defaultValue: "Amend" })}
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid={`fx-amend-popover-${tid}`}
        className="w-80 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 space-y-2"
      >
        <div className="text-[11px] font-bold text-slate-700 dark:text-slate-200">
          {t("ui.fx_amend.title", { defaultValue: "Art 45 VAT treatment" })}
        </div>
        <div className="space-y-1">
          <label className="flex items-start gap-2 text-[11px] text-slate-700 dark:text-slate-200 cursor-pointer">
            <input
              type="radio"
              data-testid={`fx-amend-inclusive-${tid}`}
              checked={treatment === "inclusive"}
              onChange={() => pickTreatment("inclusive")}
              className="mt-0.5"
            />
            <span>{t("ui.fx_amend.inclusive", { defaultValue: "Inclusive (Tulică) — extract VAT out of the stated amount" })}</span>
          </label>
          <label className="flex items-start gap-2 text-[11px] text-slate-700 dark:text-slate-200 cursor-pointer">
            <input
              type="radio"
              data-testid={`fx-amend-reinvoiced-${tid}`}
              checked={treatment === "reinvoiced"}
              onChange={() => pickTreatment("reinvoiced")}
              className="mt-0.5"
            />
            <span>{t("ui.fx_amend.reinvoiced", { defaultValue: "Re-invoiced — stated amount is net, add VAT on top" })}</span>
          </label>
        </div>
        {needsEvidence && (
          <input
            type="text"
            data-testid={`fx-amend-evidence-${tid}`}
            value={evidence}
            onChange={(e) => setEvidence(e.target.value)}
            placeholder={t("ui.fx_amend.evidence_placeholder", { defaultValue: "Credit-note / reissued-invoice reference (required)" })}
            className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 rounded px-2 py-1 text-xs tabular-nums"
          />
        )}
        <PresetReasonSelect
          testid={`fx-amend-reason-preset-${tid}`}
          presets={FX_TREATMENT_REASONS[treatment] || []}
          preset={preset}
          onPick={(p, v) => { setPreset(p); setReason(v); }}
        />
        {err && (
          <div data-testid={`fx-amend-error-${tid}`} className="text-[10px] text-red-700 dark:text-red-300">
            {err}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid={`fx-amend-cancel-${tid}`}
            onClick={() => { setOpen(false); reset(); }}
            className="text-[10px] underline text-slate-600 dark:text-slate-300"
          >
            {t("ui.fx_amend.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            data-testid={`fx-amend-submit-${tid}`}
            data-resolution-class="self-resolve"
            data-resolution-endpoint="POST /api/runs/calc/{calc_id}/reclassify-fx-treatment"
            disabled={busy || !valid}
            onClick={submit}
            className="text-[10px] font-bold px-3 py-1 rounded bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white"
          >
            {busy
              ? t("ui.fx_amend.submitting", { defaultValue: "Applying…" })
              : t("ui.fx_amend.submit", { defaultValue: "Apply treatment" })}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default FxTreatmentAmend;
